import type { GestureResponderEvent, StyleProp, ViewStyle } from 'react-native';

import { BackgroundView, type BackgroundViewProps } from '../BackgroundView';
import { flex } from '../constants';
import { Interactive } from '../Interactive';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';

export interface BaseCardProps extends BackgroundViewProps {
    /**
     * If the card should link somewhere, use this prop to give that card a link for accessibility
     * and styling purposes.
     */
    href?: string;

    /**
     * Style to append to the base card styling
     */
    style?: StyleProp<ViewStyle>;

    /**
     * Activestyle to append to the base card styling when that card is hovered. Will only apply itself
     * if the card's component prop is still an interactive component.
     */
    activeStyle?: StyleProp<ViewStyle>;

    /**
     * Component to render as the base component for this card. This container component will enable
     * feature like interactivity. Defaults to `Interactive.TouchableOpacity`.
     */
    component?: BackgroundViewProps['component'];

    /**
     * Event handler that triggers when the card is clicked.
     */
    onPress?: (event: GestureResponderEvent) => void;
}

/**
 * Base component for building cards. Will include the basic styling needed for interactive classes
 */
export function BaseCard(props: BaseCardProps) {
    const {
        children,
        onPress,
        style,
        activeStyle,
        component = Interactive.TouchableOpacity,
        href,
        ...rest
    } = props;
    const [theme, styles] = useCurrentTheme(themedStyles);

    return (
        <BackgroundView
            component={component}
            backgroundColor={theme.colors.background.main}
            style={[styles.card, flex.row, style]}
            {...(Interactive.isInteractiveComponent(component)
                ? {
                      hoverStyle: onPress ? [styles.cardHover, activeStyle] : undefined,
                      focusStyle: onPress ? [styles.cardFocused, activeStyle] : undefined,
                      onPress,
                      href,
                      handle: BaseCard.interactiveHandle,
                  }
                : {})}
            {...rest}
        >
            {children}
        </BackgroundView>
    );
}
BaseCard.interactiveHandle = Interactive.createInteractiveHandle('BaseCard');

const themedStyles = ThemedStyleSheet.create({
    card: (theme) => ({
        borderWidth: 1,
        borderColor: theme.colors.border.mid,
        borderRadius: theme.radiuses.medium,
        transitionProperty: 'all',
        overflow: 'hidden',
    }),
    cardFocused: (theme) => ({
        borderColor: theme.colors.primary.main,
    }),
    cardHover: (theme) => ({
        transform: [
            {
                translateY: -2,
            },
        ],
        ...theme.shadows.hover,
    }),
});
